import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Newspaper } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PriceAtTime } from "@/components/PriceAtTime";
import { cn, fromNowFR } from "@/lib/utils";

interface Props {
  forumId: number | string;
}

export function ForumNews({ forumId }: Props) {
  const { data = [], isLoading } = useQuery({
    queryKey: ["forum-news", forumId],
    queryFn: () => api.forumNews(forumId),
    enabled: forumId != null,
  });

  // Show the 5 most recent items.
  const items = data.slice(0, 5);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Newspaper className="size-4" />
          Actualités ({data.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {isLoading ? (
          <>
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </>
        ) : items.length === 0 ? (
          <p className="text-sm text-foreground-muted">
            Aucune actualité scrapée pour ce forum.
            <br />
            Cliquez « Rafraîchir » pour aller en chercher.
          </p>
        ) : (
          items.map((n, i) => (
            <a
              key={n.url + i}
              href={n.url}
              target="_blank"
              rel="noreferrer"
              className={cn(
                "block rounded-md border border-border p-3 hover:bg-surface-muted transition-colors"
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm leading-snug line-clamp-2">{n.title}</h3>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-muted mt-1">
                    {n.source && <span className="font-medium">{n.source}</span>}
                    <span>·</span>
                    <span>{fromNowFR(n.published_at)}</span>
                    <PriceAtTime
                      price={n.price_at_publish}
                      variation={n.variation_at_publish}
                      currency={n.price_currency}
                      source={n.price_source}
                    />
                  </div>
                  {n.description && (
                    <p className="text-xs text-foreground-muted mt-2 line-clamp-2">
                      {n.description}
                    </p>
                  )}
                </div>
                <ExternalLink className="size-3.5 text-foreground-muted shrink-0 mt-0.5" />
              </div>
            </a>
          ))
        )}
      </CardContent>
    </Card>
  );
}
